import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { Box, Tooltip, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Table } from "@4uhub/lib4uhub";
import MainApi from "../../../../../../services/mainApi.service";
import {
  IScheduleNewCheckIn,
  IScheduleNewCheckInChild,
} from "../../../../../../models/schedule-new-check-in";
import { ETasyScheduleTypeCode } from "../../../../../Schedule/model";

const SCHEDULE_NEW_CHECKIN_ROUTE =
  process.env.REACT_APP_CALENDAR + "/api/v1/TasySchedNewCheckInRule/";

const scheduleNewCheckInService = new MainApi<IScheduleNewCheckIn>(SCHEDULE_NEW_CHECKIN_ROUTE);

const translationPath = "page.settings.schedule.scheduleNewCheckIn.";

const ScheduleNewCheckInPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { eI } = useParams();

  const [data, setData] = useState<IScheduleNewCheckIn[]>([]);
  const [deletedData, setDeletedData] = useState<IScheduleNewCheckIn | null>(null);

  // Mostra os nomes dos filtros da regra; lista vazia vale para qualquer valor
  const renderFilterCell = useCallback(
    (items?: IScheduleNewCheckInChild[]) => {
      if (!items || items.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t(translationPath + "any")}
          </Typography>
        );
      }

      const names = items.map((item) => item.externalName);

      return (
        <Tooltip
          title={names.map((name) => <div key={name}>{name}</div>)}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 600,
                maxHeight: 400,
                overflowY: "auto",
                fontSize: "0.875rem",
                p: 1.5,
              },
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, width: "100%" }}>
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {names[0]}
            </Typography>
            {names.length > 1 && (
              <Typography variant="body2" sx={{ flexShrink: 0, ml: 0.5 }}>
                {`(+${names.length - 1})`}
              </Typography>
            )}
          </Box>
        </Tooltip>
      );
    },
    [t]
  );

  const columns: GridColDef<IScheduleNewCheckIn>[] = useMemo(
    () => [
      {
        field: "tasyScheduleTypeCode",
        headerName: t(translationPath + "tasy_schedule_type_code") || "Tipo de Agenda",
        width: 140,
        valueFormatter: ({ value }) =>
          t(
            translationPath +
              (value === ETasyScheduleTypeCode.EXAM ? "exam" : "consultation")
          ),
      },
      {
        field: "calendars",
        headerName: t(translationPath + "calendars") || "Agendas",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => renderFilterCell(row.calendars),
      },
      {
        field: "insurances",
        headerName: t(translationPath + "insurances") || "Convênios",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => renderFilterCell(row.insurances),
      },
      {
        field: "insuranceTypes",
        headerName: t(translationPath + "insurance_types") || "Tipos de Convênio",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => renderFilterCell(row.insuranceTypes),
      },
      {
        field: "time",
        headerName: t(translationPath + "time") || "Tempo Limite (min)",
      },
      {
        field: "metersApproximation",
        headerName: t(translationPath + "meters_approximation") || "Aproximação (m)",
      },
    ],
    [t, renderFilterCell]
  );

  const handleRowClick = (params: GridRowParams<IScheduleNewCheckIn>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const handleAdd = () => {
    navigate("new");
    setDeletedData(null);
  };

  return (
    <Table<IScheduleNewCheckIn>
      showDefaultMacroFilters={false}
      service={scheduleNewCheckInService}
      columns={columns}
      title={t(translationPath + "schedule_new_check_in") || "Regra de Check-in"}
      onAdd={handleAdd}
      onFetchData={setData}
      onFetchDelete={setDeletedData}
      onRowClick={handleRowClick}
      searchInput={false}
      backButton
      showButton={true}
      backButtonUrl={`/e/${eI}/dashboard/settings`}
      searchInputPlaceHolder={t(translationPath + "search") || "Buscar regras"}
      addButtonLabel={t(translationPath + "add_schedule_new_check_in") || "Nova Regra"}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default ScheduleNewCheckInPage;
